import type {
  DropdownMenuCheckboxItemProps as RadixDropdownMenuCheckboxItemProps,
  DropdownMenuContentProps as RadixDropdownMenuPopoverProps,
  DropdownMenuItemProps as RadixDropdownMenuItemProps,
  DropdownMenuRadioGroupProps as RadixDropdownMenuRadioGroupProps,
  DropdownMenuRadioItemProps as RadixDropdownMenuRadioItemProps,
} from '@radix-ui/react-dropdown-menu';
import type { RefObject } from 'react';
import type { To } from 'react-router';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { merge } from 'lodash-es';
import React from 'react';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { ButtonMenuArrowProp, ButtonProps } from '../../controls/Button/Button';
import type { ShortcutKey } from '../../formatting/Shortcut/Shortcut';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { ToggleInputProps } from '../ToggleInput/ToggleInput';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import {
  slideDownAndFade,
  slideLeftAndFade,
  slideRightAndFade,
  slideUpAndFade,
} from '../../common/animations';
import { zIndexes } from '../../common/z_indexes';
import { Button } from '../../controls/Button/Button';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import Shortcut from '../../formatting/Shortcut/Shortcut';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { Small } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { isEmptyNode } from '../../utilities/isEmptyNode';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { VStack } from '../../utilities/Stack/VStack';
import {
  type AccessLevelProp,
  AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import { useViewport } from '../../utilities/useViewport';
import { Badge } from '../Badge/Badge';
import { ToggleInput } from '../ToggleInput/ToggleInput';
import { Tooltip } from '../Tooltip/Tooltip';

export const DropdownMenuTrigger = RadixDropdownMenu.Trigger;

export function DropdownMenuButton({
  menuArrow = 'dropdown',
  size,
  variant = 'secondary',
  ref,
  disabled,
  truncate = false,
  ...remaining
}: Omit<ButtonProps, 'menuArrow'> & {
  ref?: React.Ref<HTMLButtonElement | null>;
  menuArrow?: ButtonMenuArrowProp | null;
}) {
  return (
    <RadixDropdownMenu.Trigger asChild disabled={disabled}>
      <Button
        {...remaining}
        ref={ref}
        disabled={disabled}
        labelStretch
        menuArrow={menuArrow ?? undefined}
        size={size}
        truncate={truncate}
        variant={variant}
      />
    </RadixDropdownMenu.Trigger>
  );
}

const DropdownMenuItemGlyph = styled(DynamicIcon, {
  color: '$$iconColor',

  variants: {
    isCheckbox: {
      true: {
        '@notDesktop': {
          width: '$14',
          height: '$14',
        },

        '@desktop': {
          width: '$10',
          height: '$10',
        },
      },
      false: {
        '@notDesktop': {
          width: '$18',
          height: '$18',
        },

        '@desktop': {
          width: '$14',
          height: '$14',
        },
      },
    },
    internal: {
      true: {
        color: '$$internalIconColor',
      },
      false: {},
    },
  },
});
DropdownMenuItemGlyph.displayName = 'DropdownMenuItemGlyph';

const DropdownMenuItemIcon = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '$4',

  '@notDesktop': {
    widthAll: '$18',
    heightAll: '$18',
  },

  '@desktop': {
    widthAll: '$14',
    heightAll: '$14',
  },

  variants: {
    checked: {
      true: {},
      false: {},
    },
    internal: {
      true: {},
      false: {},
    },
    isCheckbox: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      isCheckbox: true,
      checked: true,
      css: {
        background: colors.tokenBgBrandLight,

        [darkThemeSelector]: {
          background: colors.tokenBgBrandDark,
        },
      },
    },
    {
      isCheckbox: true,
      checked: false,
      css: {
        background: colors.tokenBgNeutralLight,

        [darkThemeSelector]: {
          background: colors.tokenBgNeutralDark,
        },
      },
    },
    {
      isCheckbox: true,
      checked: true,
      internal: true,
      css: {
        background: colors.internalTokenBgLight,

        [darkThemeSelector]: {
          background: colors.internalTokenBgDark,
        },
      },
    },
  ],
});
DropdownMenuItemIcon.displayName = 'DropdownMenuItemIcon';

const DropdownMenuItemLabel = styled(Text, {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  color: '$$labelColor',

  '@notDesktop': {
    ...LargeSansSizes,
    gap: '$8',
  },

  '@desktop': {
    ...BodySansSizes,
    gap: '$6',
  },

  variants: {
    internal: {
      true: {
        color: '$$internalLabelColor',
      },
      false: {},
    },
  },
});
DropdownMenuItemLabel.displayName = 'DropdownMenuItemLabel';

const DropdownMenuItemDescription = styled(Small, {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  color: '$$labelColor',

  variants: {
    internal: {
      true: {
        color: '$$internalLabelColor',
      },
      false: {},
    },
  },
});
DropdownMenuItemDescription.displayName = 'DropdownMenuItemDescription';

const dropdownMenuItemContainer = {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  background: '$$backgroundColor',
  cursor: 'pointer',
  $$iconColor: colors.iconNeutralLight,
  $$labelColor: colors.bodyNeutralLight,
  $$descriptionColor: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    $$iconColor: colors.iconNeutralDark,
    $$labelColor: colors.bodyNeutralDark,
    $$descriptionColor: colors.bodyNeutralDark,
  },

  '@notDesktop': {
    gap: '$8',
    padding: '$6 $10',
    borderRadius: '$10',
  },

  '@desktop': {
    gap: '$6',
    padding: '$4 $8',
    borderRadius: '$8',
  },

  '&[aria-disabled="true"]': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  '&[aria-checked="true"]': {
    zIndex: 2,
    $$iconColor: colors.iconBrandLight,
    $$labelColor: colors.bodyBrandLight,
    $$descriptionColor: colors.bodyBrandLight,

    [darkThemeSelector]: {
      $$iconColor: colors.iconBrandDark,
      $$labelColor: colors.bodyBrandDark,
      $$descriptionColor: colors.bodyBrandDark,
    },

    '&[data-highlighted]': {
      $$iconColor: colors.bodyBrandLight,
      $$labelColor: colors.headingBrandLight,
      $$descriptionColor: colors.bodyBrandLight,

      [darkThemeSelector]: {
        $$iconColor: colors.bodyBrandDark,
        $$labelColor: colors.headingBrandDark,
        $$descriptionColor: colors.bodyBrandDark,
      },
    },
  },

  '&[data-highlighted]': {
    zIndex: 3,
    outline: 'none',
    boxShadow: shadows.focusRingLight,
    $$iconColor: colors.bodyNeutralLight,
    $$labelColor: colors.headingNeutralLight,
    $$descriptionColor: colors.bodyNeutralLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
      $$iconColor: colors.bodyNeutralDark,
      $$labelColor: colors.headingNeutralDark,
      $$descriptionColor: colors.bodyNeutralDark,
    },
  },

  '&[data-highlighted][aria-checked="true"]': {
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    fill: {
      true: {},
      false: {},
    },
    selected: {
      true: {
        zIndex: 2,
        $$backgroundColor: colors.bgBrandLight,
        $$iconColor: colors.iconBrandLight,
        $$labelColor: colors.bodyBrandLight,
        $$descriptionColor: colors.bodyBrandLight,

        [darkThemeSelector]: {
          $$backgroundColor: colors.bgBrandDark,
          $$iconColor: colors.iconBrandDark,
          $$labelColor: colors.bodyBrandDark,
          $$descriptionColor: colors.bodyBrandDark,
        },

        '&[data-highlighted]': {
          $$backgroundColor: colors.bgBrandLight,
          $$iconColor: colors.bodyBrandLight,
          $$labelColor: colors.headingBrandLight,
          $$descriptionColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            $$backgroundColor: colors.bgBrandDark,
            $$iconColor: colors.bodyBrandDark,
            $$labelColor: colors.headingBrandDark,
            $$descriptionColor: colors.bodyBrandDark,
          },
        },
      },
      false: {},
    },
    variant: {
      neutral: {},
      negative: {
        $$iconColor: colors.iconNegativeLight,
        $$labelColor: colors.bodyNegativeLight,
        $$descriptionColor: colors.bodyNegativeLight,

        [darkThemeSelector]: {
          $$iconColor: colors.iconNegativeDark,
          $$labelColor: colors.bodyNegativeDark,
          $$descriptionColor: colors.bodyNegativeDark,
        },

        '&[aria-checked="true"]': {
          $$iconColor: colors.iconNegativeLight,
          $$labelColor: colors.bodyNegativeLight,
          $$descriptionColor: colors.bodyNegativeLight,

          [darkThemeSelector]: {
            $$iconColor: colors.iconNegativeDark,
            $$labelColor: colors.bodyNegativeDark,
            $$descriptionColor: colors.bodyNegativeDark,
          },

          '&[data-highlighted]': {
            $$iconColor: colors.bodyNegativeLight,
            $$labelColor: colors.headingNegativeLight,
            $$descriptionColor: colors.bodyNegativeLight,

            [darkThemeSelector]: {
              $$iconColor: colors.bodyNegativeDark,
              $$labelColor: colors.headingNegativeDark,
              $$descriptionColor: colors.bodyNegativeDark,
            },
          },
        },
      },
    },
    internal: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      fill: true,
      css: {
        '&[aria-checked="true"]': {
          background: colors.bgBrandLight,
          strokeAll: colors.strokeBrandLight,

          [darkThemeSelector]: {
            background: colors.bgBrandDark,
            strokeAll: colors.strokeBrandDark,
          },
        },
      },
    },
    {
      selected: true,
      fill: true,
      css: {
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
        },
      },
    },
    {
      variant: 'negative',
      fill: true,
      css: {
        '&[aria-checked="true"]': {
          background: colors.bgNegativeLight,
          strokeAll: colors.strokeNegativeLight,

          [darkThemeSelector]: {
            background: colors.bgNegativeDark,
            strokeAll: colors.strokeNegativeDark,
          },
        },
      },
    },
    {
      internal: true,
      css: {
        $$internalIconColor: colors.internalIconLight,
        $$internalLabelColor: colors.internalBodyLight,
        $$internalDescriptionColor: colors.internalBodyLight,

        [darkThemeSelector]: {
          $$internalIconColor: colors.internalIconDark,
          $$internalLabelColor: colors.internalBodyDark,
          $$internalDescriptionColor: colors.internalBodyDark,
        },

        '&[aria-checked="true"]': {
          $$internalIconColor: colors.internalBodyLight,
          $$internalLabelColor: colors.internalHeadingLight,
          $$internalDescriptionColor: colors.internalBodyLight,

          [darkThemeSelector]: {
            $$internalIconColor: colors.internalBodyDark,
            $$internalLabelColor: colors.internalHeadingDark,
            $$internalDescriptionColor: colors.internalBodyDark,
          },
        },

        '&[data-highlighted]': {
          $$internalIconColor: colors.internalBodyLight,
          $$internalLabelColor: colors.internalHeadingLight,
          $$internalDescriptionColor: colors.internalBodyLight,

          [darkThemeSelector]: {
            $$internalIconColor: colors.internalBodyDark,
            $$internalLabelColor: colors.internalHeadingDark,
            $$internalDescriptionColor: colors.internalBodyDark,
          },
        },
      },
    },
    {
      internal: true,
      fill: true,
      css: {
        '&[aria-checked="true"]': {
          background: colors.internalBgLight,
          strokeAll: colors.internalStrokeLight,

          [darkThemeSelector]: {
            background: colors.internalBgDark,
            strokeAll: colors.internalStrokeDark,
          },
        },
      },
    },
  ],
};

type DropdownMenuItemInnerPropsWithoutRouting = {
  accessLevel?: AccessLevelProp | null;
  annotation?: React.ReactNode;
  description?: React.ReactNode;
  endAdornment?: React.ReactNode;
  icon?: DynamicIconName;
  internal?: boolean;
  isCheckbox?: boolean;
  label?: React.ReactNode;
  selected?: boolean;
  tooltip?: React.ReactNode;
  shortcut?: ShortcutKey[];
  variant?: 'neutral' | 'negative';
};

type DropdownMenuItemInnerProps =
  | (DropdownMenuItemInnerPropsWithoutRouting & { as?: undefined; to?: undefined })
  | (DropdownMenuItemInnerPropsWithoutRouting & { as: React.ElementType; to?: To });

function DropdownMenuItemInner({
  accessLevel: accessLevelProp,
  annotation,
  description,
  endAdornment,
  icon,
  internal: internalProp,
  isCheckbox,
  label,
  tooltip,
  selected,
  shortcut,
  variant,
  // Routing
  as,
  to,
}: DropdownMenuItemInnerProps) {
  const { breakpoint } = useViewport();
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const internal = internalProp || accessLevel === AccessLevel.Internal;

  const hasStart = Boolean(icon || isCheckbox);
  const hasEnd = Boolean(accessLevel || annotation || shortcut || endAdornment);

  const labelContents = (
    <DropdownMenuItemLabel internal={internal} context="body" variant={variant} weight="bold">
      {label}
    </DropdownMenuItemLabel>
  );

  return (
    <AlignStack
      as={as}
      to={to}
      gap={breakpoint !== 'desktop' ? 8 : 6}
      preset={breakpoint !== 'desktop' ? 'large' : 'body'}
      start={
        hasStart && (
          <DropdownMenuItemIcon
            isCheckbox={isCheckbox}
            checked={Boolean(selected)}
            internal={internal}
          >
            {(icon || selected) && (
              <DropdownMenuItemGlyph
                isCheckbox={isCheckbox}
                internal={internal}
                icon={icon ?? 'placeholder'}
                variant={variant}
              />
            )}
          </DropdownMenuItemIcon>
        )
      }
      end={
        hasEnd && (
          <>
            {accessLevel && isAccessLevelVisible(accessLevel) && (
              <FeatureBadge
                size={breakpoint !== 'desktop' ? 'medium' : 'small'}
                type={accessLevel}
              />
            )}
            {annotation && (
              <Badge
                internal={internal}
                size={breakpoint !== 'desktop' ? 'medium' : 'small'}
                variant={selected ? 'brand' : variant}
              >
                {annotation}
              </Badge>
            )}
            {shortcut && (
              <Shortcut
                internal={internal}
                keys={shortcut}
                variant={variant === 'negative' ? 'destructive' : undefined}
              />
            )}
            {endAdornment}
          </>
        )
      }
    >
      <VStack spacing={2} width="full">
        {label && (tooltip ? <Tooltip contents={tooltip}>{labelContents}</Tooltip> : labelContents)}
        {description && (
          <DropdownMenuItemDescription internal={internal} variant={variant}>
            {description}
          </DropdownMenuItemDescription>
        )}
      </VStack>
    </AlignStack>
  );
}

export type DropdownMenuItemProps = RadixDropdownMenuItemProps &
  DropdownMenuItemInnerProps & { ref?: React.Ref<HTMLDivElement> };

const DropdownMenuItemTarget = styled('div', {
  width: '100%',
});
DropdownMenuItemTarget.displayName = 'DropdownMenuItemTarget';

const DropdownMenuItemContainer = styled(RadixDropdownMenu.Item, {
  ...dropdownMenuItemContainer,
});
DropdownMenuItemContainer.displayName = 'DropdownMenuItemContainer';

export function DropdownMenuItem({
  accessLevel: accessLevelProp,
  asChild,
  annotation,
  children,
  description,
  icon,
  internal: internalProp,
  label,
  ref,
  selected,
  shortcut,
  tooltip,
  variant,
  //
  as,
  to,
  disabled,
  ...remaining
}: DropdownMenuItemProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const internal = internalProp || accessLevel === AccessLevel.Internal;
  const preventDisabledNav = disabled ? (e: React.MouseEvent) => e.preventDefault() : undefined;

  return (
    <DropdownMenuItemContainer
      internal={internal}
      ref={ref}
      {...remaining}
      disabled={disabled}
      asChild={asChild || Boolean(as || to)}
      fill
      selected={selected}
      role="menuitem"
    >
      <DropdownMenuItemTarget as={as || 'div'} to={to} onClick={preventDisabledNav}>
        <DropdownMenuItemInner
          accessLevel={accessLevelProp}
          annotation={annotation}
          description={description}
          icon={icon}
          internal={internalProp}
          label={label || children}
          selected={selected}
          shortcut={shortcut}
          variant={variant}
          tooltip={tooltip}
        />
      </DropdownMenuItemTarget>
    </DropdownMenuItemContainer>
  );
}

const DropdownMenuCheckboxItemContainer = styled(RadixDropdownMenu.CheckboxItem, {
  ...dropdownMenuItemContainer,
});
DropdownMenuCheckboxItemContainer.displayName = 'DropdownMenuCheckboxItemContainer';

export type DropdownCheckboxItemProps = RadixDropdownMenuCheckboxItemProps &
  DropdownMenuItemInnerProps & {
    ref?: RefObject<HTMLDivElement | null>;
    hideCheckmark?: boolean;
  };

export function DropdownMenuCheckboxItem({
  accessLevel: accessLevelProp,
  checked,
  children,
  annotation,
  description,
  hideCheckmark,
  icon,
  internal: internalProp,
  label,
  ref,
  shortcut,
  tooltip,
  ...remaining
}: DropdownCheckboxItemProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const internal = internalProp || accessLevel === AccessLevel.Internal;

  return (
    <DropdownMenuCheckboxItemContainer
      checked={checked}
      fill={false}
      internal={internal}
      ref={ref}
      {...remaining}
    >
      <DropdownMenuItemInner
        isCheckbox={!hideCheckmark}
        accessLevel={accessLevelProp}
        annotation={annotation}
        description={description}
        icon={checked ? 'checkmark' : icon}
        selected={Boolean(checked)}
        internal={internalProp}
        label={label || children}
        shortcut={shortcut}
        tooltip={tooltip}
      />
    </DropdownMenuCheckboxItemContainer>
  );
}

export type DropdownMenuToggleItemProps = RadixDropdownMenuCheckboxItemProps &
  Omit<DropdownMenuItemInnerProps, 'selected'> & {
    ref?: RefObject<HTMLDivElement | null>;
    /**
     * When true (default), the menu stays open when the toggle is clicked.
     * When false, the menu closes after toggle.
     */
    keepOpenOnToggle?: boolean;
    /** Props passed to the ToggleInput (variant, size, etc.) */
    toggleProps?: Pick<ToggleInputProps, 'variant' | 'size'>;
  };

const DropdownMenuToggleItemWrapper = styled('div', {
  display: 'flex',
  paddingLeft: '$4',
  pointerEvents: 'none',
});

export function DropdownMenuToggleItem({
  accessLevel: accessLevelProp,
  checked,
  children,
  annotation,
  description,
  icon,
  internal: internalProp,
  keepOpenOnToggle = true,
  label,
  onCheckedChange,
  onSelect,
  ref,
  shortcut,
  tooltip,
  toggleProps,
  variant,
  ...remaining
}: DropdownMenuToggleItemProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const internal = internalProp || accessLevel === AccessLevel.Internal;

  return (
    <DropdownMenuCheckboxItemContainer
      checked={checked}
      fill
      internal={internal}
      ref={ref}
      onCheckedChange={onCheckedChange}
      onSelect={(event: Event) => {
        if (keepOpenOnToggle) {
          event.preventDefault();
        }
        onSelect?.(event);
      }}
      {...remaining}
    >
      <DropdownMenuItemInner
        accessLevel={accessLevelProp}
        annotation={annotation}
        description={description}
        endAdornment={
          <DropdownMenuToggleItemWrapper>
            <ToggleInput size="small" selected={Boolean(checked)} {...toggleProps} />
          </DropdownMenuToggleItemWrapper>
        }
        icon={icon}
        internal={internalProp}
        label={label || children}
        shortcut={shortcut}
        tooltip={tooltip}
        variant={variant}
      />
    </DropdownMenuCheckboxItemContainer>
  );
}

const DropdownMenuRadioItemContainer = styled(RadixDropdownMenu.RadioItem, {
  ...dropdownMenuItemContainer,
});
DropdownMenuRadioItemContainer.displayName = 'DropdownMenuRadioItemContainer';

export type DropdownRadioItemProps = RadixDropdownMenuRadioItemProps &
  DropdownMenuItemInnerProps & { ref?: RefObject<HTMLDivElement | null> };

export function DropdownMenuRadioItem({
  accessLevel: accessLevelProp,
  children,
  description,
  icon,
  internal: internalProp,
  label,
  ref,
  shortcut,
  tooltip,
  ...remaining
}: DropdownRadioItemProps) {
  const accessLevel = useAccessLevelContext(accessLevelProp);
  const internal = internalProp || accessLevel === AccessLevel.Internal;

  if (accessLevel === null) return null;

  return (
    <DropdownMenuRadioItemContainer fill internal={internal} ref={ref} {...remaining}>
      <DropdownMenuItemInner
        accessLevel={accessLevelProp}
        description={description}
        icon={icon}
        internal={internalProp}
        label={label || children}
        shortcut={shortcut}
        tooltip={tooltip}
      />
    </DropdownMenuRadioItemContainer>
  );
}

const DropdownMenuLabel = styled(RadixDropdownMenu.Label, Small, {
  color: colors.controlContentPlaceholderLight,

  '@notDesktop': {
    padding: '$6 $10',
  },

  '@desktop': {
    padding: '$4 $8',
  },

  [darkThemeSelector]: {
    color: colors.controlContentPlaceholderDark,
  },
});
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

const dropdownMenuGroupContainer = {
  display: 'flex',
  flexDirection: 'column',

  '@notDesktop': {
    padding: '$8',
  },

  '@desktop': {
    padding: '$6',
  },

  '&:not(:last-child)': {
    strokeBottom: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      strokeBottom: colors.strokeNeutralDark,
    },
  },
};

const DropdownMenuGroupContainer = styled(RadixDropdownMenu.Group, {
  ...dropdownMenuGroupContainer,

  variants: {
    scrollable: {
      true: {
        overflow: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      },
      false: {
        flex: '0 0 auto',
      },
    },
    isNested: {
      true: {
        '@notDesktop': {
          padding: '$8 0',
        },

        '@desktop': {
          padding: '$6 0',
        },
      },
    },
  },
});
DropdownMenuGroupContainer.displayName = 'DropdownMenuGroupContainer';

type DropdownMenuGroupProps = {
  children: React.ReactNode;
  label?: React.ReactNode;
};

export function DropdownMenuGroup({
  children,
  isNested = false,
  label,
  scrollable = false,
  ...remaining
}: {
  children: React.ReactNode;
  isNested?: boolean;
  label?: React.ReactNode;
  scrollable?: boolean;
}) {
  // Don't render empty groups
  if (isEmptyNode(children)) {
    return null;
  }

  return (
    <DropdownMenuGroupContainer
      isNested={isNested}
      scrollable={scrollable}
      data-scrollable={scrollable ? 'true' : undefined}
      {...remaining}
    >
      {label && <DropdownMenuLabel>{label}</DropdownMenuLabel>}
      {children}
    </DropdownMenuGroupContainer>
  );
}

const DropdownMenuRadioGroupContainer = styled(RadixDropdownMenu.RadioGroup, {
  ...dropdownMenuGroupContainer,

  variants: {
    scrollable: {
      true: {
        overflow: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      },
      false: {
        flex: '0 0 auto',
      },
    },
  },
});
DropdownMenuRadioGroupContainer.displayName = 'DropdownMenuRadioGroupContainer';

export function DropdownMenuRadioGroup({
  children,
  label,
  scrollable = false,
  ...remaining
}: RadixDropdownMenuRadioGroupProps &
  DropdownMenuGroupProps & {
    scrollable?: boolean;
  }) {
  return (
    <DropdownMenuRadioGroupContainer
      scrollable={scrollable}
      data-scrollable={scrollable ? 'true' : undefined}
      {...remaining}
    >
      {label && <DropdownMenuLabel>{label}</DropdownMenuLabel>}
      {children}
    </DropdownMenuRadioGroupContainer>
  );
}

const DropdownMenuPopoverContainer = styled(RadixDropdownMenu.Content, {
  zIndex: zIndexes.overlay,
  display: 'flex',
  flexDirection: 'column',
  background: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  borderRadius: '$10',

  minWidth: '$180',
  maxWidth: 'min($400, var(--radix-dropdown-menu-content-available-width))',
  maxHeight: 'var(--radix-dropdown-menu-content-available-height)',

  '@mobile': {
    width: 'var(--radix-dropdown-menu-content-available-width)',
  },

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },

  '@media (prefers-reduced-motion: no-preference)': {
    animationDuration: '150ms',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'forwards',
    willChange: 'transform, opacity',

    '&[data-state="open"]': {
      '&[data-side="top"]': { animationName: slideUpAndFade },
      '&[data-side="right"]': { animationName: slideRightAndFade },
      '&[data-side="bottom"]': { animationName: slideDownAndFade },
      '&[data-side="left"]': { animationName: slideLeftAndFade },
    },
  },

  variants: {
    hasInnerScroll: {
      true: {},
      false: {
        overflow: 'auto',
      },
    },
    matchTrigger: {
      true: {
        minWidth: 'var(--radix-dropdown-menu-trigger-width)',
      },
      false: {},
    },
    belowDialog: {
      true: {
        zIndex: zIndexes.globalOverlay,
      },
      false: {},
    },
  },
});
DropdownMenuPopoverContainer.displayName = 'DropdownMenuPopoverContainer';

export type DropdownMenuPopoverProps = RadixDropdownMenuPopoverProps & {
  container?: HTMLElement | null | undefined;
  /**
   * If true, will allow the inner content to scroll when inside a scrollable group.
   * @default false
   */
  hasInnerScroll?: boolean;
  /**
   * If true, will match the width of the trigger.
   * @default false
   */
  matchTrigger?: boolean;
  /**
   * If false, will render the contents in place instead of portalling to `document.body`.
   *
   * @default true
   */
  usePortal?: boolean;
  /**
   * If set, will render the popover below any dialogs. For global or application-wide controls.
   */
  belowDialog?: boolean;
  width?: SpacingScale;
  maxWidth?: SpacingScale;
  ref?: React.Ref<HTMLDivElement | null>;
};

export function DropdownMenuPopover({
  align = 'end',
  children,
  container,
  collisionPadding = 12,
  hasInnerScroll = false,
  matchTrigger = false,
  side,
  sideOffset = 4,
  usePortal = true,
  width,
  maxWidth,
  ref: forwardedRef,
  ...props
}: DropdownMenuPopoverProps) {
  const { breakpoint } = useViewport();
  const defaultAlign = breakpoint !== 'mobile' ? 'end' : 'center';

  const css = merge(widthCSS(width), maxWidthCSS(maxWidth));

  const contents = (
    <DropdownMenuPopoverContainer
      align={align || defaultAlign}
      collisionPadding={collisionPadding}
      hasInnerScroll={hasInnerScroll}
      matchTrigger={matchTrigger}
      side={side}
      sideOffset={sideOffset}
      css={css}
      {...props}
      ref={forwardedRef}
    >
      {children}
    </DropdownMenuPopoverContainer>
  );

  return usePortal ? (
    <RadixDropdownMenu.Portal container={container}>{contents}</RadixDropdownMenu.Portal>
  ) : (
    contents
  );
}

export const DropdownMenu = RadixDropdownMenu.Root;
